import React, { useContext, useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { LoginContext } from '../../reactcontext/ReactContext';
import axios from 'axios';
import {
  AiFillHome,
  AiFillCaretDown,
  AiFillSetting,
  AiOutlineUser,
} from "react-icons/ai";
import { MdManageAccounts } from "react-icons/md";
import { FaFirstAid } from "react-icons/fa";
import {
  BiSolidReport,
  BiHelpCircle,
  BiUser,
  BiDollar,
  BiLogoVisa,
  BiBook,
  BiBuildingHouse,
} from "react-icons/bi";

const PermissionBasedNav = () => {
  const { loginInfo } = useContext(LoginContext);
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDaysLinks, setShowDaysLinks] = useState(false);
  const [showMonthsLinks, setShowMonthsLinks] = useState(false);
  const [showPaymentLinks, setShowPaymentLinks] = useState(false);
  const [showMedicalLinks, setShowMedicalLinks] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (loginInfo.login) {
      fetchUserPermissions();
    }
  }, [loginInfo]);

  const fetchUserPermissions = async () => {
    try {
      // Admin users don't need to fetch permissions - they have all permissions
      if (loginInfo.userDetails?.role?.toLowerCase() === 'admin') {
        console.log('User is admin, setting all permissions');
        setUserPermissions([]);
        setLoading(false);
        return;
      }

      console.log('Fetching permissions for user:', loginInfo.userDetails);

      const response = await axios.get(`${apiUrl}/permission/user`, {
        headers: { loginToken: localStorage.getItem('loginToken') }
      });

      console.log('Permissions response:', response.data);
      setUserPermissions(response.data.permissions || []);
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      setUserPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const hasPermission = (permissionName) => {
    // Admin users have all permissions
    if (loginInfo.userDetails?.role?.toLowerCase() === 'admin') {
      return true;
    }
    const hasPerm = userPermissions.some(perm => perm.name === permissionName);
    console.log(`Checking permission "${permissionName}":`, hasPerm, 'Available permissions:', userPermissions.map(p => p.name));
    return hasPerm;
  };

  const hasAnyPermission = (permissionNames) => {
    return permissionNames.some(name => hasPermission(name));
  };

  // Navigation items with their required permissions
  const navItems = [
    {
      key: "dashboard",
      name: "Dashboard",
      icon: <AiFillHome fontSize={18} />,
      path: "/dashboard",
      permission: null // Always visible
    },
    {
      key: "profile",
      name: "Employee",
      icon: <BiUser fontSize={18} />,
      path: "/profile",
      permission: "employees.view"
    },
    {
      key: "visa_exp",
      name: "Visa Expire",
      icon: <BiLogoVisa fontSize={18} />,
      path: "/visa_exp",
      permission: "employees.view",
      hasSubmenu: true
    },
    {
      key: "passport_exp",
      name: "Passport Expire",
      icon: <BiBook fontSize={18} />,
      path: "/passport_exp",
      permission: "employees.view",
      hasSubmenu: true
    },
    {
      key: "medical",
      name: "Medical",
      icon: <FaFirstAid fontSize={18} />,
      path: "/medical",
      permission: "employees.view",
      hasSubmenu: true
    },
    {
      key: "agents",
      name: "Agents",
      icon: <BiBuildingHouse fontSize={18} />,
      path: "/agents",
      permission: "agents.view"
    },
    {
      key: "payments",
      name: "Payments",
      icon: <BiDollar fontSize={18} />,
      path: "/payments",
      permission: "payments.view",
      hasSubmenu: true
    },
    {
      key: "users",
      name: "Manage Users",
      icon: <MdManageAccounts fontSize={18} />,
      path: "/users",
      permission: "users.view"
    },
    {
      key: "reports",
      name: "Reports",
      icon: <BiSolidReport fontSize={18} />,
      path: "/reports",
      permission: "reports.view"
    },
    {
      key: "my-profile",
      name: "My Profile",
      icon: <AiOutlineUser fontSize={18} />,
      path: "/my-profile",
      permission: null // Always visible
    },
    {
      key: "support",
      name: "Help",
      icon: <BiHelpCircle fontSize={18} />,
      path: "/support",
      permission: "support.view"
    }
  ];

  const visaSubMenus = [
    { key: "30_days", name: "30 Days", path: "/30_days", permission: "employees.view" },
    { key: "60_days", name: "60 Days", path: "/60_days", permission: "employees.view" },
    { key: "90_days", name: "90 Days", path: "/90_days", permission: "employees.view" },
    { key: "expired", name: "Expired Visa", path: "/expired", permission: "employees.view" },
  ];

  const passportSubMenus = [
    { key: "1_month", name: "1 Month", path: "/1_month", permission: "employees.view" },
    { key: "3_month", name: "3 Month", path: "/3_month", permission: "employees.view" },
    { key: "6_month", name: "6 Month", path: "/6_month", permission: "employees.view" },
    { key: "pass_expired", name: "Pass Expired", path: "/pass_expired", permission: "employees.view" },
  ];

  const paymentSubMenus = [
    { key: "payments", name: "Payments List", path: "/payments", permission: "payments.view", end: true },
    { key: "update", name: "Update Payment", path: "/payments/update", permission: "payments.edit" },
    { key: "record", name: "Record Payment", path: "/payments/record", permission: "payments.create" },
    { key: "invoice", name: "Invoice", path: "/payments/invoice", permission: "payments.invoice" },
  ];

  const medicalSubMenus = [
    { key: "medical", name: "Medical List", path: "/medical", permission: "employees.view", end: true },
    { key: "need-mc", name: "Need MC", path: "/medical/need-mc", permission: "employees.view" },
  ];

  const handleMenuClick = (key) => {
    if (key === "visa_exp") {
      setShowDaysLinks(!showDaysLinks);
      setShowMonthsLinks(false);
      setShowPaymentLinks(false);
      setShowMedicalLinks(false);
    } else if (key === "passport_exp") {
      setShowDaysLinks(false);
      setShowMonthsLinks(!showMonthsLinks);
      setShowPaymentLinks(false);
      setShowMedicalLinks(false);
    } else if (key === "payments") {
      setShowDaysLinks(false);
      setShowMonthsLinks(false);
      setShowPaymentLinks(!showPaymentLinks);
      setShowMedicalLinks(false);
    } else if (key === "medical") {
      setShowDaysLinks(false);
      setShowMonthsLinks(false);
      setShowPaymentLinks(false);
      setShowMedicalLinks(!showMedicalLinks);
    } else {
      setShowDaysLinks(false);
      setShowMonthsLinks(false);
      setShowPaymentLinks(false);
      setShowMedicalLinks(false);
    }
  };

  const linkActiveStyles = "opacity-100 text-[12pt] font-medium flex flex-row items-center gap-x-2.5 p-2.5 bg-blue-50 text-blue-700 rounded-lg transition-all duration-200 border-l-4 border-blue-600";
  const linkNotActiveStyles = "opacity-75 text-[12pt] flex flex-row items-center gap-x-2.5 p-2.5 hover:opacity-100 hover:bg-gray-100 rounded-lg transition-all duration-200";

  if (loading) {
    return (
      <div className="p-4 text-center text-gray-500">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
        <p className="mt-2 text-sm">Loading navigation...</p>
      </div>
    );
  }

  // Admin users should see all navigation items
  const isAdmin = loginInfo.userDetails?.role?.toLowerCase() === 'admin';

  console.log('PermissionBasedNav - isAdmin:', isAdmin);
  console.log('PermissionBasedNav - userDetails:', loginInfo.userDetails);

  return (
    <div className="flex flex-col gap-y-1.5 overflow-y-auto">
      {navItems.map((item) => {
        // Check if user has permission for this item
        // Admin users have access to everything, others need specific permissions
        if (!isAdmin && item.permission && !hasPermission(item.permission)) {
          return null;
        }

        const isExpanded = (item.key === "visa_exp" && showDaysLinks) ||
                          (item.key === "passport_exp" && showMonthsLinks) ||
                          (item.key === "payments" && showPaymentLinks) ||
                          (item.key === "medical" && showMedicalLinks);

        return (
          <div key={item.key}>
            <NavLink
              to={item.path}
              onClick={() => handleMenuClick(item.key)}
              className={({ isActive }) =>
                isActive ? linkActiveStyles : linkNotActiveStyles
              }
            >
              {item.icon}
              <span>{item.name}</span>
              {item.hasSubmenu && (
                <AiFillCaretDown
                  fontSize={14}
                  className={`ml-auto transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                />
              )}
            </NavLink>

            {/* Render submenus */}
            {item.key === "visa_exp" && showDaysLinks && (
              <div className="ml-2 mt-2 bg-gray-50 rounded-lg p-2 border-l-2 border-blue-300">
                {visaSubMenus.map((sub) => (
                  (isAdmin || hasPermission(sub.permission)) && (
                    <NavLink
                      key={sub.key}
                      to={sub.path}
                      className={({ isActive }) =>
                        isActive ? "text-[12pt] font-medium flex flex-row items-center gap-x-2.5 p-2.5 bg-blue-100 text-blue-700 rounded-md transition-all duration-200 mb-1.5" : "text-[12pt] opacity-70 flex flex-row items-center gap-x-2.5 p-2.5 hover:opacity-100 hover:bg-gray-200 rounded-md transition-all duration-200 mb-1.5"
                      }
                    >
                      <BiLogoVisa fontSize={16} />
                      <span>{sub.name}</span>
                    </NavLink>
                  )
                ))}
              </div>
            )}

            {item.key === "passport_exp" && showMonthsLinks && (
              <div className="ml-2 mt-2 bg-gray-50 rounded-lg p-2 border-l-2 border-blue-300">
                {passportSubMenus.map((sub) => (
                  (isAdmin || hasPermission(sub.permission)) && (
                    <NavLink
                      key={sub.key}
                      to={sub.path}
                      className={({ isActive }) =>
                        isActive ? "text-[12pt] font-medium flex flex-row items-center gap-x-2.5 p-2.5 bg-blue-100 text-blue-700 rounded-md transition-all duration-200 mb-1.5" : "text-[12pt] opacity-70 flex flex-row items-center gap-x-2.5 p-2.5 hover:opacity-100 hover:bg-gray-200 rounded-md transition-all duration-200 mb-1.5"
                      }
                    >
                      <BiBook fontSize={16} />
                      <span>{sub.name}</span>
                    </NavLink>
                  )
                ))}
              </div>
            )}

            {item.key === "payments" && showPaymentLinks && (
              <div className="ml-2 mt-2 bg-gray-50 rounded-lg p-2 border-l-2 border-blue-300">
                {paymentSubMenus.map((sub) => (
                  (isAdmin || hasPermission(sub.permission)) && (
                    <NavLink
                      key={sub.key}
                      to={sub.path}
                      end={!!sub.end}
                      className={({ isActive }) =>
                        isActive ? "text-[12pt] font-medium flex flex-row items-center gap-x-2.5 p-2.5 bg-blue-100 text-blue-700 rounded-md transition-all duration-200 mb-1.5" : "text-[12pt] opacity-70 flex flex-row items-center gap-x-2.5 p-2.5 hover:opacity-100 hover:bg-gray-200 rounded-md transition-all duration-200 mb-1.5"
                      }
                    >
                      <BiDollar fontSize={16} />
                      <span>{sub.name}</span>
                    </NavLink>
                  )
                ))}
              </div>
            )}

            {item.key === "medical" && showMedicalLinks && (
              <div className="ml-2 mt-2 bg-gray-50 rounded-lg p-2 border-l-2 border-blue-300">
                {medicalSubMenus.map((sub) => (
                  (isAdmin || hasPermission(sub.permission)) && (
                    <NavLink
                      key={sub.key}
                      to={sub.path}
                      end={!!sub.end}
                      className={({ isActive }) =>
                        isActive ? "text-[12pt] font-medium flex flex-row items-center gap-x-2.5 p-2.5 bg-blue-100 text-blue-700 rounded-md transition-all duration-200 mb-1.5" : "text-[12pt] opacity-70 flex flex-row items-center gap-x-2.5 p-2.5 hover:opacity-100 hover:bg-gray-200 rounded-md transition-all duration-200 mb-1.5"
                      }
                    >
                      <FaFirstAid fontSize={16} />
                      <span>{sub.name}</span>
                    </NavLink>
                  )
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PermissionBasedNav;